import React, { useContext, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import { Single_Section } from '@graphql/queries'
import { AppContext } from '@state/context'
import { closePanel, openPanel } from '@state/actions/panelsActions'
import {
  setCurrentSection,
  setSelectedSectionId,
} from '@state/actions/appActions'
import { useUpdateSection } from '@hooks/mutations/useUpdateSection'
import { getSubjectTextClass } from '@helpers/subject'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  BUTTON_TYPE,
  HEADING_TYPE,
  TEXT_SIZES,
  CONTENT_TYPES,
} from '@helpers/constants'
import Button from '@components/Button'
import Text from '@components/Text'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import ButtonWithLoader from '@components/ButtonWithLoader'
import Loader from '@components/Loader'
import Icon from '@components/Icon'
import { useConfirm } from '@hooks/useConfirm'
import SubjectAreaList from '@components/SubjectAreaList'
import BlockRenderer from '@views/BlockRenderer'
import { useLocation } from 'react-router-dom'

const ViewSectionPanel = () => {
  const [state, dispatch] = useContext(AppContext)
  const {
    app: { selectedSectionId: sectionId, userId },
  } = state
  const location = useLocation()
  const onSectionPage = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[1] === 'avsnitt' && parts[2] === sectionId
  }, [location, sectionId])

  const { data, error } = useSWR([Single_Section, sectionId])
  const [section, setSection] = useState(null)

  useEffect(() => {
    if (data && data.section && !error) setSection(data.section)
  }, [data, error])

  const isSaved = section?.customers.some((customer) => customer?.id === userId)
  const { post, isPosting } = useUpdateSection()
  const { confirm } = useConfirm()

  const updateSection = async (
    connectCustomers = [],
    disconnectCustomers = []
  ) => {
    if (disconnectCustomers.length > 0) {
      const confirmed = await confirm({
        title: section.name,
        text: 'Är du säker på att du vill ta bort det här avsnittet från din bokhylla?',
        confirmText: 'Ja, ta bort avsnitt',
        cancelText: 'Avbryt',
      })
      if (!confirmed) return
    }
    const res = await post(
      {
        id: sectionId,
        connectCustomers: connectCustomers,
        disconnectCustomers: disconnectCustomers,
      },
      connectCustomers.length > 0
        ? 'Sparad i bokhyllan.'
        : 'Borttagen från bokhyllan.',
      connectCustomers.length > 0
        ? 'Nåt gick fel när avsnittet skulle sparas i bokhyllan.'
        : 'Nåt gick fel när avsnittet skulle tas bort från bokhyllan.'
    )
  }

  return (
    <div>
      {!data && !error && <Loader />}
      {error && <Text>Nåt gick fel när vi skulle ladda avsnittet</Text>}
      {section && (
        <>
          <div className={'flex justify-between'}>
            <div className={'flex items-center'}>
              <Icon
                icon={'Section'}
                color={getSubjectTextClass(section.subject.slug, 'dark')}
              />
              <Text
                color={getSubjectTextClass(section.subject.slug, 'dark')}
                size={TEXT_SIZES.BASE}
                extraClasses={'ml-2'}
              >
                {section.subject.name}
              </Text>
            </div>
            <Button
              handleClick={() => {
                dispatch(closePanel(PANELS.VIEW_SECTION))
              }}
              size={BUTTON_SIZE.NO_PADDING}
              icon={'Close'}
            ></Button>
          </div>

          <div>
            <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
              {section.name}
            </Heading>
            <BlockRenderer content={section?.description} />
          </div>
          <Divider size={DIVIDER_SIZE.SMALL} />

          <div className={'flex'}>
            <Text extraClasses={'mr-2'} bold>
              Årskurs:
            </Text>
            <Text>{section.schoolYear.name}</Text>
          </div>

          <div className={'flex'}>
            <Text extraClasses={'mr-2'} bold>
              Nivå:
            </Text>
            <Text>{section.level.name}</Text>
          </div>

          <Divider size={DIVIDER_SIZE.SMALL} />

          <SubjectAreaList
            contentType={CONTENT_TYPES.SECTION}
            item={section}
          />

          <Divider size={DIVIDER_SIZE.SMALL} />

          {!onSectionPage &&
            <>
              <Button
                type={BUTTON_TYPE.SUBJECT}
                subject={section?.subject?.slug}
                to={`/avsnitt/${sectionId}`}
                size={BUTTON_SIZE.LARGE}
                handleClick={() => {
                  dispatch(closePanel(PANELS.VIEW_BOOK))
                }}
              >
                Öppna
              </Button>
              <Divider size={DIVIDER_SIZE.SMALL} />
            </>
          }

          <div className={'flex'}>
            <Button
              size={BUTTON_SIZE.LARGE}
              type={BUTTON_TYPE.DARK_GREY}
              handleClick={() => {
                dispatch(setSelectedSectionId(sectionId))
                dispatch(setCurrentSection(section))
                dispatch(openPanel(PANELS.ADD_SECTION_TO_BOOK))
              }}
              icon={'Book'}
              iconRight
              marginRight
            >
              Lägg till i bok
            </Button>

            <ButtonWithLoader
              type={BUTTON_TYPE.DARK_GREY}
              isLoading={isPosting}
              enabledText={
                !isSaved ? 'Spara i bokhyllan' : 'Ta bort från bokhyllan'
              }
              disabled={isPosting}
              size={BUTTON_SIZE.LARGE}
              handleClick={() => {
                !isSaved
                  ? updateSection([userId], []) // connect
                  : updateSection([], [userId]) // disconnect
              }}
              icon={isSaved ? 'Exit' : 'Favourite'}
              iconRight
            />
          </div>
        </>
      )}
    </div>
  )
}

export default ViewSectionPanel
